import { Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { deleteShapes } from '../../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import { ShapeState } from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { addUngroupedShape } from '../../../../cross-team-shared/state/shape.state';
import { delayOnMicrotaskQueue } from '../../../../shared/misc-utilities/misc-utils';
import { ShapeGroupOrderState } from '../../../../team-apis/content/content-connector';
import {
  HORIZONTAL_HAIR_INDEX,
  VERTICAL_HAIR_INDEX,
} from '../../../../team-apis/core/reserved-shape-indexes';
import {
  Point2D,
  VectorTaggingShapeType,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { AbstractSensorContentStore2D } from '../../state/abstract-sensor-content-store-2d';
import { ImageBoundsService } from '../edit/image-bounds.service';

@Injectable()
export class CrosshairService extends InjectableSuperclass {
  private mouseSubscription?: Subscription;

  constructor(
    private store: AbstractSensorContentStore2D,
    private imageBoundsService: ImageBoundsService,
  ) {
    super();
  }

  showCrosshair() {
    if (this.mouseSubscription) {
      return; // the crosshair is already showing
    }

    this.store.batch(() => {
      const vectorOverlay = this.store('vectorOverlay');
      addHair(VERTICAL_HAIR_INDEX, vectorOverlay);
      addHair(HORIZONTAL_HAIR_INDEX, vectorOverlay);
    });

    this.mouseSubscription = this.store('mouse')('imagePoint')
      .$.pipe(
        filter(isDefined),
        // Because we will change the store in response to a change in the
        // store, delay to avoid https://github.com/ngrx/platform/issues/2322
        delayOnMicrotaskQueue(),
      )
      .subscribe(this.updateCrosshair.bind(this));
  }

  hideCrosshair() {
    if (!this.mouseSubscription) {
      return; // the hairs are already hidden
    }

    this.store.batch(() => {
      deleteShapes(
        [VERTICAL_HAIR_INDEX, HORIZONTAL_HAIR_INDEX],
        this.store('vectorOverlay'),
      );
    });
    this.mouseSubscription.unsubscribe();
    this.mouseSubscription = undefined;
  }

  private async updateCrosshair([x, y]: Point2D) {
    if (isDefined(this.store.state().vectorOverlay.shapes[0])) {
      // fix me if you change this code
      // eslint-disable-next-line no-param-reassign -- fix me!
      [x, y] = this.imageBoundsService.boundPoints([x, y]);
    } else {
      // fix me if you change this code
      // eslint-disable-next-line no-param-reassign -- fix me!
      [x, y] = this.imageBoundsService.snapToImage([x, y]);
    }
    this.store.batch(() => {
      const shapesStore = this.store('vectorOverlay')('shapes');
      moveHair(shapesStore(VERTICAL_HAIR_INDEX), [
        [x, -999999],
        [x, 999999],
      ]);
      moveHair(shapesStore(HORIZONTAL_HAIR_INDEX), [
        [-999999, y],
        [999999, y],
      ]);
    });
  }
}

function addHair(index: number, overlayStore: Store<ShapeGroupOrderState>) {
  addUngroupedShape(
    {
      type: 'hair',
      index,
      tags: {},
      dynamic_tags: {},
      key_locations: [
        {
          frame_number: 0,
          visibility: 1,
          points: [
            [-1, -1],
            [-1, -1],
          ],
        },
      ],
    },
    overlayStore,
  );
}

function moveHair(
  shapeStore: Store<ShapeState<Point2D, VectorTaggingShapeType>>,
  points: Point2D[],
) {
  shapeStore('key_locations')(0)('points').set(points);
}
